import { Editor, Node, Path, Transforms } from 'slate';

import * as serializer from 'basic-editor-value-serializer';

import { findNearestLayout } from './boundary';
import { countLayoutAncestors } from './wrap';

const layoutUtilities = serializer.utilities.node.layout;

function clearItemSettings(editor, path) {

  const properties = { ...Node.get(editor, path).properties };

  delete properties.layoutItem;
  Transforms.setNodes(editor, { properties }, { at: path });

}

function isInRange(rowPath, firstIndex, lastIndex, node, path) {

  if (Path.isChild(path, rowPath) === false) {
    return false;
  }

  const index = path[path.length - 1];

  return index >= firstIndex && index <= lastIndex;

}

// The row item that holds the caret, or null when the caret is not in a row.
export function captureRowItem(editor) {

  if (editor.selection === null) {
    return null;
  }

  const path = editor.selection.anchor.path;
  const layoutEntry = findNearestLayout(editor, path);

  if (layoutEntry === null || layoutUtilities.isRowLayout(layoutEntry[0]) === false) {
    return null;
  }

  const itemPath = path.slice(0, layoutEntry[1].length + 1);

  return {
    itemPath,
    index: itemPath[itemPath.length - 1],
    count: layoutEntry[0].children.length,
    rowReference: Editor.pathRef(editor, layoutEntry[1]),
    itemReference: Editor.pathRef(editor, itemPath)
  };

}

export function canStackItem(editor, itemPath) {
  return layoutUtilities.canNest(countLayoutAncestors(editor, itemPath) + 1, Node.get(editor, itemPath));
}

// Whatever an action added next to a row item goes into an auto stack with that item, which keeps
// the item's size, so the row never gains an item.
export function keepNewBlocksInItem(editor, capture) {

  const rowPath = capture.rowReference.unref();
  const itemPath = capture.itemReference.unref();

  if (rowPath === null || itemPath === null) {
    return;
  }

  const added = Node.get(editor, rowPath).children.length - capture.count;

  if (added <= 0) {
    return;
  }

  const firstPath = rowPath.concat(capture.index);
  const itemSettings = (Node.get(editor, itemPath).properties || {}).layoutItem;
  const stack = { ...layoutUtilities.buildStackNode([serializer.utilities.node.paragraph.buildNode()]), children: [] };

  Editor.withoutNormalizing(editor, function wrapAdded() {

    for (let offset = 0; offset <= added; offset++) {
      clearItemSettings(editor, rowPath.concat(capture.index + offset));
    }

    Transforms.wrapNodes(editor, stack, { at: rowPath, match: isInRange.bind(null, rowPath, capture.index, capture.index + added) });

    if (typeof itemSettings !== 'undefined') {
      Transforms.setNodes(editor, { properties: { ...Node.get(editor, firstPath).properties, layoutItem: itemSettings } }, { at: firstPath });
    }

  });

}

// Runs an insert at the caret. Blocks it adds to a row are kept in the caret's item; returns false,
// inserting nothing, when the auto stack would break the depth limit.
export function insertKeepingRowItem(editor, addsBlocks, insert) {

  const capture = captureRowItem(editor);

  if (capture === null || addsBlocks === false) {
    insert();
    return true;
  }

  if (canStackItem(editor, capture.itemPath) === false) {
    capture.rowReference.unref();
    capture.itemReference.unref();
    return false;
  }

  Editor.withoutNormalizing(editor, function insertInItem() {
    insert();
    keepNewBlocksInItem(editor, capture);
  });
  return true;

}
